import React, { useMemo } from 'react'
import Headline from 'redesign/components/atoms/Headline'
import { HEADLINE_LEVELS } from 'redesign/components/atoms/Headline/constants'
import EventsSection from 'redesign/components/organisms/ShiftPlanSections/EventsSection'
import NoShiftTenantSection from 'redesign/components/organisms/ShiftPlanSections/NoShiftTenantSection'
import ShiftSection from 'redesign/components/organisms/ShiftPlanSections/ShiftSection'
import TeamSection from 'redesign/components/organisms/ShiftPlanSections/TeamSection/TeamSection'
import EmptyPage from 'redesign/components/organisms/empty-pages'
import { EMPTY_PAGE_VARIANTS } from 'redesign/components/organisms/empty-pages/constants'
import { ZERO } from 'shared/constants'
import { getTodayFormatted } from 'utils/date'
import ShiftPlanInfo from './shift-plan-info'
import { Container, EmptyPageContainer } from './styles'
import type { ShiftPlanContentProps } from './types'
import useShiftPlanContent from './useShiftPlanContent'

const ShiftPlanContent: React.FC<ShiftPlanContentProps> = ({
  sliderParams,
  sortedTenantIds,
  showTenantHeadline,
  tenantList,
  groupedTenants,
  getAbsenceRequestsByTenantId,
  effectiveShifts,
  effectiveShiftsLoading,
  effectiveShiftsError,
  shiftUpdates,
  onTaskClick,
  getEventsForTenantShiftCard,
  date,
  shiftsSectionLoading,
  teamPlanData,
  teamPlanTenants,
  teamPlanLoading,
  onTeamClick,
  eventsLoading,
  showNoShiftTenant,
  filteredPersonsWithTenant,
}) => {
  const isPast = useMemo(() => Boolean(date && date < getTodayFormatted()), [date])
  const shiftPlanData = useShiftPlanContent({
    sortedTenantIds,
    tenantList,
    groupedTenants,
    getAbsenceRequestsByTenantId,
    effectiveShifts,
    effectiveShiftsLoading,
    shiftUpdates,
    getEventsForTenantShiftCard,
    isPast,
    date,
    teamPlanData,
  })
  const isEmptyPage = useMemo(
    () =>
      shiftPlanData.length === ZERO && teamPlanTenants?.length === ZERO && Object.keys(teamPlanData)?.length === ZERO,
    [shiftPlanData, teamPlanData, teamPlanTenants],
  )

  const isFullyLoaded = useMemo(
    () => !eventsLoading && !teamPlanLoading && !shiftsSectionLoading,
    [eventsLoading, shiftsSectionLoading, teamPlanLoading],
  )

  if (isEmptyPage && isFullyLoaded) {
    return (
      <EmptyPageContainer>
        <EmptyPage variant={EMPTY_PAGE_VARIANTS.noShifts} isCentered />
      </EmptyPageContainer>
    )
  }

  return showNoShiftTenant ? (
    <NoShiftTenantSection
      date={date}
      isPast={isPast}
      getAbsenceRequestsByTenantId={getAbsenceRequestsByTenantId}
      onTaskClick={onTaskClick}
      filteredPersonsWithTenant={filteredPersonsWithTenant}
    />
  ) : (
    <>
      {shiftPlanData.map(
        ({
          tenant,
          shiftData,
          events,
          showEvents,
          showShifts,
          absenceRequests,
          groupedShifts,
          filteredEffectiveShifts,
          canShowEffectiveTimes,
          showShiftUpdateAlert,
          updateTask,
          shiftUpdateDaysCount,
          teamPlan,
        }) => {
          const hasShifts = groupedShifts.length > ZERO

          return (
            <Container
              key={`${tenant.id}-${date}-${sliderParams.index}`}
              data-testid={`shift-card__container--${date}`}
            >
              <ShiftPlanInfo
                showTenantHeadline={showTenantHeadline}
                showEvents={showEvents}
                isFullyLoaded={isFullyLoaded}
                showShifts={showShifts}
                canShowEffectiveTimes={canShowEffectiveTimes}
                hasShifts={hasShifts}
                teamPlan={teamPlan}
              >
                {showTenantHeadline && (
                  <Headline
                    testId={`${tenant.name}-headline`}
                    level={HEADLINE_LEVELS.first}
                    text={tenant.name}
                    noBottomSpacing
                  />
                )}
                {showEvents && isFullyLoaded && (
                  <EventsSection eventsLoading={eventsLoading} events={events} date={date} />
                )}
                {Boolean(showShifts) && (
                  <ShiftSection
                    date={date}
                    isPast={isPast}
                    shiftData={shiftData}
                    tenant={tenant}
                    absenceRequests={absenceRequests}
                    groupedShifts={groupedShifts}
                    onTaskClick={onTaskClick}
                    effectiveShifts={filteredEffectiveShifts}
                    effectiveShiftsError={Boolean(effectiveShiftsError)}
                    canShowEffectiveTimes={canShowEffectiveTimes}
                    showShiftUpdateAlert={showShiftUpdateAlert}
                    updateTask={updateTask}
                    shiftsSectionLoading={shiftsSectionLoading}
                    shiftUpdateDaysCount={shiftUpdateDaysCount}
                  />
                )}
                {!showShiftUpdateAlert && (
                  <TeamSection loading={teamPlanLoading} teamPlan={teamPlan} onTeamClick={onTeamClick} />
                )}
              </ShiftPlanInfo>
            </Container>
          )
        },
      )}
    </>
  )
}

export default ShiftPlanContent
